import { Fragment, useEffect, useMemo, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import { rage, useInputAction, useObservable } from '~/rage';
import { InputAction, Layouts } from '~/shared';
import { ActionButton } from '../../components/ActionButton';
import { BanInfo } from '../../components/BanInfo';
import { Chat } from '../../components/Chat';
import { HeadInfo } from '../../components/HeadInfo';
import { HelpButtons } from '../../components/HelpButtons';
import { Hint } from '../../components/Hint';
import { InteractDot } from '../../components/InteractDot';
import { JailInfo } from '../../components/JailInfo';
import { MapInfo } from '../../components/MapInfo';
import { Offer } from '../../components/Offer';
import { PlayerStats } from '../../components/PlayerStats';
import { PlaytimeReward } from '../../components/PlaytimeReward';
import { Quests } from '../../components/Quests';
import { ServerInfo } from '../../components/ServerInfo';
import { Speedometer } from '../../components/Speedometer';
import { XPInfo } from '../../components/XPInfo';

const Hud: React.FC = () => {
	const speedoVisible = useObservable(rage.vehicles.isPlayerVehicleDriver());
	const isLeftSideHidden = useObservable(
		rage.gui.getLayoutVisibleState(Layouts.PlayerVehicles),
	);
	const helpButtonsVisible = useObservable(
		rage.player.getHudHelpButtonsVisible(),
	);
	const interactVisible = useObservable(rage.game.getInteractState());
	const visibleLayouts = useObservable(rage.gui.getVisibleLayouts());
	const hint = useObservable(rage.hud.getHint());

	const phoneVisible = useMemo(() => {
		return visibleLayouts.includes(Layouts.Phone);
	}, [visibleLayouts]);

	useInputAction(InputAction.HudKeysHelpShow, () => {
		void rage.player
			.setHudHelpButtonsVisible(!helpButtonsVisible)
			.catch(() => undefined);
	});

	const [isDebugVisible, setIsDebugVisible] = useState(true);

	useEffect(() => {
		if (rage.config.isStub()) {
			const handleKeyDown = (event: KeyboardEvent) => {
				if (event.key === '1') {
					setIsDebugVisible((prev) => !prev);
				}
			};

			window.addEventListener('keydown', handleKeyDown);

			return () => {
				window.removeEventListener('keydown', handleKeyDown);
			};
		}
	}, []);

	if (rage.config.isStub() && !isDebugVisible) {
		return null;
	}

	return (
		<Fragment>
			{!isLeftSideHidden && <ServerInfo />}
			{!isLeftSideHidden && <Chat />}
			{!isLeftSideHidden && <BanInfo />}
			<MapInfo />
			<HeadInfo />
			<XPInfo />
			<PlayerStats />
			<PlaytimeReward />
			<Quests />
			<ActionButton />
			<HelpButtons visible={helpButtonsVisible && !phoneVisible} />
			{speedoVisible ? <Speedometer /> : null}
			<CSSTransition
				in={interactVisible}
				classNames={'interact'}
				timeout={300}
				appear={true}
				unmountOnExit
			>
				<InteractDot />
			</CSSTransition>
			<Offer suppressed={!!hint} />
			<Hint />
			<JailInfo />
		</Fragment>
	);
};

export { Hud };
